import React, { useRef, useState, useEffect } from 'react';
import './index.scss';
import ThreeMonitor from '../XMonitor/Monitor/ThreeMonitor';
import ControlBar from '../ControlBar';

interface SceneReplayerProps {
  videoUrl?: string;
  eventId: number; // 对应场景id
  eventType: string; // 'scene'
  startTime?: string; // 开始时间
  endTime?: string; // 结束时间
}

const SceneReplayer = (props: SceneReplayerProps) => {
  const _videoRef = useRef<any>(null);
  const _interval = useRef<any>(null);
  const _eventStartTime = useRef<number>(0);
  const _eventEndTime = useRef<number>(0);
  const _initAddress = useRef<boolean>(true);

  const [newEventStartTime, setNewEventStartTime] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(true);
  const [currentTime, setCurrentTime] = useState<number>(-100);
  const [duration, setDuration] = useState<number>(0);
  const [replayKey, setReplayKey] = useState<number>(Date.now());
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 初始化时间范围
  useEffect(() => {
    if (props.startTime && props.endTime) {
      _eventStartTime.current = new Date(props.startTime).getTime();
      _eventEndTime.current = new Date(props.endTime).getTime();
      setDuration(_eventEndTime.current - _eventStartTime.current);
    }
  }, [props.startTime, props.endTime]);

  // 视频加载完成后获取时长并自动播放
  useEffect(() => {
    if (props.videoUrl && _initAddress.current) {
      const timer = setInterval(() => {
        if (_videoRef.current?.readyState > 0) {
          _initAddress.current = false;
          clearInterval(timer);

          // 如果没有传入时间范围，从视频本身获取时长
          if (!props.startTime || !props.endTime) {
            const videoDuration = _videoRef.current.duration * 1000; // 转换为毫秒
            setDuration(videoDuration);
            _eventStartTime.current = 0;
            _eventEndTime.current = videoDuration;
          }

          // 直接在这里执行播放逻辑，避免依赖外部函数
          clearInterval(_interval.current);
          setNewEventStartTime(_eventStartTime.current);
          _videoRef.current?.play();
          setIsPaused(false);
          _interval.current = setInterval(() => {
            setCurrentTime((currentTime) => currentTime + 100);
          }, 100);
        }
      }, 200);

      return () => clearInterval(timer);
    }
  }, [props.videoUrl, props.startTime, props.endTime]);

  // 监听播放结束
  useEffect(() => {
    if (currentTime >= duration && duration > 0) {
      handleEventPause();
    }
  }, [currentTime, duration]);

  // 添加空格键控制（只绑定一次）
  useEffect(() => {
    const onPressSpace = (event: KeyboardEvent) => {
      if (event.code === 'Space') {
        _videoRef.current?.paused ? handleEventPlay() : handleEventPause();
      }
    };

    window.addEventListener('keyup', onPressSpace);
    return () => {
      window.removeEventListener('keyup', onPressSpace);
    };
  }, []); // 空依赖数组，只绑定一次

  // 添加视频事件监听器
  useEffect(() => {
    const video = _videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      // 如果没有传入时间范围，从视频本身获取时长
      if (!props.startTime || !props.endTime) {
        const videoDuration = video.duration * 1000; // 转换为毫秒
        setDuration(videoDuration);
        _eventStartTime.current = 0;
        _eventEndTime.current = videoDuration;
      }
    };

    const handleTimeUpdate = () => {
      // 可以在这里同步视频的实际播放时间（可选）
      // setCurrentTime(video.currentTime * 1000);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [props.videoUrl, props.startTime, props.endTime]);

  const handleEventPlay = () => {
    clearInterval(_interval.current);

    // 如果已经播放完毕，重置到开始位置而不是重新加载组件
    if (currentTime >= duration) {
      setCurrentTime(0);
      setNewEventStartTime(_eventStartTime.current);
      if (_videoRef.current) {
        _videoRef.current.currentTime = 0;
      }
    } else if (currentTime > 0 && currentTime < duration) {
      setNewEventStartTime(_eventStartTime.current + currentTime);
    }

    _videoRef.current?.play();
    setIsPaused(false);
    _interval.current = setInterval(() => {
      setCurrentTime((currentTime) => currentTime + 100);
    }, 100);
  };

  const handleEventPause = () => {
    clearInterval(_interval.current);
    _videoRef.current?.pause();
    setIsPaused(true);
    if (_videoRef.current) {
      _videoRef.current.currentTime = currentTime / 1000;
    }
    setNewEventStartTime(_eventStartTime.current + currentTime);
  };

  const handleReplay = () => {
    setReplayKey(Date.now());
    _initAddress.current = true;
    clearInterval(_interval.current);
    setIsPaused(true);
    setCurrentTime(-100);
    setNewEventStartTime(0);
  };

  const handleControlChange = (value: any) => {
    if (!_videoRef.current) {
      return;
    }
    setCurrentTime(value);
    clearInterval(_interval.current);
    if (_videoRef.current) {
      _videoRef.current.currentTime = value / 1000;
    }
    _videoRef.current?.pause();
    setNewEventStartTime(_eventStartTime.current + value);
    setIsPaused(true);
  };

  // 全屏功能
  const handleFullscreen = () => {
    if (!_videoRef.current) return;

    if (!isFullscreen) {
      // 进入全屏
      if (_videoRef.current.requestFullscreen) {
        _videoRef.current.requestFullscreen();
      } else if (_videoRef.current.webkitRequestFullscreen) {
        _videoRef.current.webkitRequestFullscreen();
      } else if (_videoRef.current.msRequestFullscreen) {
        _videoRef.current.msRequestFullscreen();
      }
    } else {
      // 退出全屏
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
    }
  };

  // 监听全屏状态变化
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);
  return (
    <div className="scene-replay-container" key={replayKey}>
      <div className="content-wrapper">
        <ThreeMonitor
          key={`three-monitor-${props.eventId}`} // 使用 key 强制重新创建组件
          url={process.env.JDX_APP_SHADOW_MONITOR_URL + '/local_views'}
          hiddenDelay={true}
          hiddenCarInfo={true}
          openData={{
            eventType: 'LOCAL_VIEW',
            data: {
              eventId: props.eventId,
              eventType: props.eventType,
              rate: 100,
            },
          }}
          sendData={{
            eventType: 'LOCAL_VIEW',
            data:
              newEventStartTime > 0
                ? {
                    eventId: props.eventId,
                    rate: 100,
                    startTime: newEventStartTime,
                    eventType: props.eventType,
                    pause: isPaused,
                  }
                : {
                    eventId: props.eventId,
                    eventType: props.eventType,
                    rate: 100,
                  },
          }}
        />
        <div className="video-content">
          {props.videoUrl && (
            <>
              <video
                ref={_videoRef}
                src={props.videoUrl}
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'fill',
                }}
              />
              <i className="full-screen-btn" onClick={handleFullscreen}></i>
            </>
          )}
        </div>
      </div>
      <div className="control-wrapper">
        <ControlBar
          currentTime={currentTime}
          duration={duration}
          handleEventPlay={handleEventPlay}
          handleEventPause={handleEventPause}
          isPaused={isPaused}
          handleControlChange={handleControlChange}
        />
      </div>
    </div>
  );
};

export default SceneReplayer;
